/**
 * Assistant analytics on the web side (CU-14zefhy26bz wave 3): reply feedback, UI event batching, "Did this solve it?",
 * retry / escalate, message ids and the first-open notice. Pure helpers (no Angular) so they can be unit tested; the
 * component only wires them to the template and the AssistantService. Everything here is optional and never blocks the chat.
 */

export type Thumb = '+1' | '-1';
export type Resolution = 'yes' | 'partly' | 'no';

/** Thumbs-down reasons: the codes match the API's `[a-z0-9_]{1,32}` rule. */
export const FEEDBACK_REASONS: {code: string; label: string}[] = [
    {code: 'wrong_data', label: 'Wrong data'},
    {code: 'didnt_understand', label: "Didn't understand"},
    {code: 'incomplete', label: 'Incomplete'},
    {code: 'couldnt_do_it', label: "Couldn't do it"},
    {code: 'too_slow', label: 'Too slow'},
    {code: 'other', label: 'Other'}
];

export const MAX_FEEDBACK_COMMENT = 500;

/** What the panel keeps on a reply after the user used the thumbs. */
export interface ReplyFeedback {
    thumb?: Thumb;
    /** the reason chips are open under the reply (thumbs-down, not yet sent) */
    open?: boolean;
    reasons?: string[];
    comment?: string;
    /** the thumb reached the server */
    sent?: boolean;
}

/** `m` + epoch ms + 4 random chars: unique in a chat and sortable; the clock and the random source are injectable for tests. */
export function newMessageId(now: number = Date.now(), random: () => number = Math.random): string {
    return (
        'm' +
        now.toString(36) +
        Math.floor(random() * 36 ** 4)
            .toString(36)
            .padStart(4, '0')
    );
}

/** Body of `POST api/assistant/feedback` for a thumb on a reply. */
export function thumbBody(turnId: string, thumb: Thumb, reasons?: string[], comment?: string): any {
    const body: any = {turnId, kind: 'thumb', value: thumb};
    const codes = (reasons || []).filter((r) => FEEDBACK_REASONS.some((x) => x.code === r));
    if (codes.length) {
        body.reasons = codes;
    }
    const text = (comment || '').trim().slice(0, MAX_FEEDBACK_COMMENT);
    if (text) {
        body.comment = text;
    }
    return body;
}

/** Adds or removes a reason code (the chips toggle). */
export function toggleReason(reasons: string[] | undefined, code: string): string[] {
    const list = reasons || [];
    return list.includes(code) ? list.filter((r) => r !== code) : [...list, code];
}

/** Body of `POST api/assistant/feedback` for the outcome of a chat. */
export function resolutionBody(value: Resolution): any {
    return {kind: 'resolution', value};
}

// ----- UI events -------------------------------------------------------------------------------------------------

export const EVENT_FLUSH_MS = 10000;
/** the API takes at most 50 events per request */
export const MAX_EVENTS_PER_REQUEST = 50;
/** a buffer that cannot be sent (offline) keeps only the newest events */
export const MAX_BUFFERED_EVENTS = 200;
/** the API accepts event names of `[A-Za-z0-9_.:-]{1,64}` */
const EVENT_NAME = /^[A-Za-z0-9_.:-]{1,64}$/;

export interface UiEvent {
    name: string;
    props?: any;
    /** ISO-8601 instant of the click */
    ts: string;
}

export interface UiEventBatch {
    session: string;
    turnId?: string;
    events: UiEvent[];
}

interface Buffered extends UiEvent {
    session: string;
    turnId?: string;
}

/** Collects UI events and hands them out as requests: one per chat (and turn), at most 50 events each. */
export class UiEventBuffer {
    private items: Buffered[] = [];

    get size(): number {
        return this.items.length;
    }

    add(session: string, name: string, props?: any, turnId?: string, now: Date = new Date()): void {
        if (!session || !EVENT_NAME.test(name || '')) {
            return;
        }
        const item: Buffered = {session, name, ts: now.toISOString()};
        if (props && Object.keys(props).length) {
            item.props = props;
        }
        if (turnId) {
            item.turnId = turnId;
        }
        this.items.push(item);
        if (this.items.length > MAX_BUFFERED_EVENTS) {
            this.items.splice(0, this.items.length - MAX_BUFFERED_EVENTS);
        }
    }

    /** Empties the buffer into requests, in the order the events happened. */
    take(): UiEventBatch[] {
        const items = this.items;
        this.items = [];
        const batches: UiEventBatch[] = [];
        for (const item of items) {
            let batch = batches.find((b) => b.session === item.session && b.turnId === item.turnId && b.events.length < MAX_EVENTS_PER_REQUEST);
            if (!batch) {
                batch = {session: item.session, turnId: item.turnId, events: []};
                batches.push(batch);
            }
            batch.events.push({name: item.name, props: item.props, ts: item.ts});
        }
        return batches;
    }
}

// ----- "Did this solve it?" ---------------------------------------------------------------------------------------

/** Idle time after the last completed turn before the question is offered. */
export const RESOLUTION_IDLE_MS = 10 * 60 * 1000;
/** The card goes away on its own after this long without an answer. */
export const RESOLUTION_AUTO_DISMISS_MS = 20 * 1000;

interface TurnMessage {
    role: string;
    pending?: boolean;
    error?: boolean;
    note?: boolean;
    screen?: any;
    stopped?: boolean;
    createdAt?: number;
    completedAt?: number;
}

/** Replies that finished normally (not failed, stopped, still running, local notes or screen cards). */
export function completedReplies(messages: TurnMessage[] | undefined): TurnMessage[] {
    return (messages || []).filter((m) => m && m.role === 'assistant' && !m.pending && !m.error && !m.note && !m.screen && !m.stopped);
}

export function completedTurns(messages: TurnMessage[] | undefined): number {
    return completedReplies(messages).length;
}

/** When the last turn completed (the message stamp), or null when unknown. */
export function lastCompletedAt(messages: TurnMessage[] | undefined): number | null {
    const list = completedReplies(messages);
    const m = list.length ? list[list.length - 1] : undefined;
    const last = m ? (m.completedAt ?? m.createdAt) : undefined;
    return typeof last === 'number' ? last : null;
}

export interface ResolutionState {
    messages: TurnMessage[] | undefined;
    /** the question was already shown (or answered) in this conversation */
    asked?: boolean;
    /** a turn is running now */
    sending?: boolean;
    /** the user closed the tab (`close`) or the chat sat idle (`idle`) */
    trigger: 'close' | 'idle';
    now: number;
    /** when the chat was last touched (a turn finished or the user sent something) */
    lastActivityAt?: number | null;
}

/** At most once per conversation, never during a running turn, only after >= 1 completed turn; idle needs 10 minutes. */
export function shouldAskResolution(s: ResolutionState): boolean {
    if (s.asked || s.sending || completedTurns(s.messages) < 1) {
        return false;
    }
    if (s.trigger === 'close') {
        return true;
    }
    const since = s.lastActivityAt ?? lastCompletedAt(s.messages);
    return since !== null && since !== undefined && s.now - since >= RESOLUTION_IDLE_MS;
}

// ----- retry / escalate / entry point ------------------------------------------------------------------------------

/** Request fields added to a turn only when set (an older API ignores them). */
export interface TurnExtras {
    escalate?: boolean;
    entryPoint?: string;
    clientMessageId?: string;
    /** CU-14zefhy38pt C5: 'fast' or 'thorough'; left out for auto (the router decides) */
    tierPreference?: string;
}

/** The API's entry points we send: `typed` is the default and is left out. */
export type EntryPoint = 'typed' | 'suggestion' | 'error_toast' | 'bug';

export function turnExtras(escalate: boolean, entryPoint?: string, clientMessageId?: string, tierPreference?: string): TurnExtras {
    const extras: TurnExtras = {};
    if (escalate) {
        extras.escalate = true;
    }
    if (entryPoint && entryPoint !== 'typed') {
        extras.entryPoint = entryPoint;
    }
    if (clientMessageId) {
        extras.clientMessageId = clientMessageId;
    }
    if (tierPreference) {
        extras.tierPreference = tierPreference;
    }
    return extras;
}

/**
 * Where a message came from: a suggestion / error-toast prompt counts only while the draft is still exactly that prompt,
 * so a rewritten text is `typed`.
 */
export function entryPointOf(source: EntryPoint | undefined, sourceText: string, draft: string): EntryPoint {
    return source && source !== 'typed' && sourceText && sourceText === draft ? source : 'typed';
}

interface RetryMessage {
    role: string;
    text: string;
    display?: string;
    error?: boolean;
    retryOf?: string;
    pending?: boolean;
}

/** The text a Retry resends: only on the last message, when it is a failed turn (not stopped) that remembers its question. */
export function retryText(messages: RetryMessage[] | undefined, index: number): string | null {
    const list = messages || [];
    const m = list[index];
    if (!m || index !== list.length - 1 || m.role !== 'assistant' || !m.error || m.pending || !m.retryOf) {
        return null;
    }
    return m.retryOf;
}

// ----- first-open notice -------------------------------------------------------------------------------------------

export const NOTICE_PREFIX = 'hm-assistant-notice-v1';
export const NOTICE_TEXT = 'Conversations are recorded to improve HaulierMagic.';

/** Per tenant + login (a shared browser asks each user once); null when the user is not known yet. */
export function noticeKey(identity: any): string | null {
    const login = identity && typeof identity === 'object' ? String(identity.login ?? identity.email ?? identity.id ?? '').trim() : '';
    if (!login) {
        return null;
    }
    return `${NOTICE_PREFIX}:${String(identity.tenantId ?? '').trim()}:${login.toLowerCase()}`;
}

type NoticeStore = Pick<Storage, 'getItem' | 'setItem'>;

export function noticeDismissed(key: string | null, store?: NoticeStore): boolean {
    if (!key) {
        return true; // nothing is stored for an unknown user: no banner either
    }
    try {
        return (store || localStorage).getItem(key) !== null;
    } catch (e) {
        return true; // storage blocked: do not nag on every open
    }
}

export function dismissNotice(key: string | null, now: Date = new Date(), store?: NoticeStore): void {
    if (!key) {
        return;
    }
    try {
        (store || localStorage).setItem(key, now.toISOString());
    } catch (e) {
        // storage unavailable: the banner stays hidden for this page load only
    }
}

// ----- rate / stop / retry / resolution flows (pure state changes the component calls) ------------------------------

interface RatedMessage {
    role?: string;
    feedback?: ReplyFeedback;
}

/** A thumbs-down on a reply with no later user message: its "better model next turn" request has not been used yet. */
export function hasOpenDownvote(messages: RatedMessage[] | undefined, except?: RatedMessage): boolean {
    const list = messages || [];
    let lastUser = -1;
    list.forEach((x, i) => {
        if (x && x.role === 'user') {
            lastUser = i;
        }
    });
    return list.some((x, i) => x !== except && i > lastUser && x?.feedback?.thumb === '-1');
}

/**
 * The user rated a reply. Returns false when nothing changes (same thumb again). A thumbs-down asks for the better model on
 * the next turn; taking it back (thumbs-up) clears that request unless another reply still holds an unused thumbs-down.
 */
export function applyRating(messages: RatedMessage[] | undefined, m: RatedMessage, thumb: Thumb, session: {escalateNext?: boolean}): boolean {
    if (m.feedback?.thumb === thumb) {
        return false;
    }
    m.feedback = {thumb, open: thumb === '-1', reasons: [], comment: '', sent: false};
    if (thumb === '-1') {
        session.escalateNext = true;
    } else if (!hasOpenDownvote(messages, m)) {
        session.escalateNext = false;
    }
    return true;
}

/** The body of the second feedback row (reasons / comment), or null when the user chose nothing; closes the chips either way. */
export function reasonsBody(turnId: string, fb: ReplyFeedback): any | null {
    const body = fb.reasons?.length || (fb.comment || '').trim() ? thumbBody(turnId, '-1', fb.reasons, fb.comment) : null;
    fb.open = false;
    fb.comment = '';
    return body;
}

interface StoppableMessage {
    pending?: boolean;
    stopped?: boolean;
    taskId?: string;
}

/** Stop: marks the bubble stopped, asks the server; a refusal (404: not running any more) puts the bubble back while it still runs. */
export async function requestStop(m: StoppableMessage, call: () => Promise<any>): Promise<boolean> {
    if (!m.pending || !m.taskId || m.stopped) {
        return false;
    }
    m.stopped = true;
    try {
        await call();
    } catch (e) {
        if (m.pending) {
            m.stopped = false;
        }
    }
    return true;
}

/** What a poll answer `cancelled` means: the user's own Stop is not an error ('Stopped.'); null = any other cancel (a failure). */
export function cancelledOutcome(m: {stopped?: boolean}): {text: string; error: boolean} | null {
    return m.stopped ? {text: 'Stopped.', error: false} : null;
}

/** The queue entry of a Retry: the same text, the screens / screenshots the failed turn carried, the better model. */
export function retryEntry<T>(text: string, frozen: T[] | undefined): {text: string; pending: T[]; escalate: true; retry: true; entryPoint: 'typed'} {
    return {text, pending: frozen ? [...frozen] : [], escalate: true, retry: true, entryPoint: 'typed'};
}

/** Whether a turn goes out with `escalate` (a Retry, or a thumbs-down earlier in this chat). */
export function wantsEscalate(queued: {escalate?: boolean}, session: {escalateNext?: boolean}): boolean {
    return !!queued.escalate || !!session.escalateNext;
}

/** The turn started: the better model was asked for once, so the thumbs-down request is used up. */
export function escalateUsed(session: {escalateNext?: boolean}): void {
    session.escalateNext = false;
}

/** Once per chat: marks the chat asked and returns true only the first time the rules allow. */
export function claimResolution(session: {resolutionAsked?: boolean}, state: ResolutionState): boolean {
    if (!shouldAskResolution({...state, asked: session.resolutionAsked})) {
        return false;
    }
    session.resolutionAsked = true;
    return true;
}
